import { lazy } from 'react';
import { Outlet } from 'react-router';
import { AntdLayout } from '@/components';
import appRoutes from '@/configs/routes';
import { PrivateRoute } from '@/components/HasRights/HasRights';
import { FEATURE_SHOW_OLD_NEWS } from '@/const/features';
import { Permissions } from '@/const/permissions';
import { SOURCES_MAP } from '@/const/sources';
import RequireAuth from '../../domains/Auth/components/RequireAuth';
import { FEEDS_SOURCE_MAP } from '../../domains/Content/pages/Feeds/PageFeeds.const';
import { FEATURES_SOURCE_MAP } from '../../domains/Featured/pages/PageFeatures/PageFeatures.const';
import { collectionsRoutes } from './CollectionsRoutes';
import { dictionariesRoutes } from './DictionariesRoutes';
import { featuresRoutes } from './FeaturesRoutes';
import { popupsRoutes } from './PopupsRoutes';

const Error404 = lazy(() => import('../../components/Errors/404'));
const PageStickers = lazy(() => import('@/domains/Other/pages/Stickers/PageStickers'));
const PageStickerCreate = lazy(() => import('@/domains/Other/pages/Stickers/PageStickerCreate'));
const PageStickerDetails = lazy(() => import('@/domains/Other/pages/Stickers/PageStickerDetails'));
const PageStickerEdit = lazy(() => import('@/domains/Other/pages/Stickers/PageStickerEdit'));
const PageLogin = lazy(() => import('../../domains/Auth/pages/PageLogin'));
const PageUsers = lazy(() => import('../../domains/RBAC/pages/Users/PageUsers'));
const PageProfile = lazy(() => import('../../domains/Profile/pages/PageProfile'));
const PagePersonalInformation = lazy(() => import('../../domains/Profile/pages/PagePersonalInformation'));
const PageUserDetails = lazy(() => import('../../domains/RBAC/pages/Users/PageUserDetails'));
const PagePermissions = lazy(() => import('../../domains/RBAC/pages/Permissions/PagePermissions'));
const PageRoles = lazy(() => import('../../domains/RBAC/pages/Roles/PageRoles'));
const PageRoleDetails = lazy(() => import('../../domains/RBAC/pages/Roles/PageRoleDetails'));
const PageLanguages = lazy(() => import('../../domains/Translations/pages/Languages/PageLanguages/PageLanguages'));
const PageStructures = lazy(() => import('../../domains/Translations/pages/Structures/PageStructures'));
const PageStructuresDetails = lazy(() => import('../../domains/Translations/pages/Structures/PageStructuresDetails'));
const PagePushIcons = lazy(() => import('../../domains/Other/pages/Push icons/PagePushIcons'));
const PageImages = lazy(() => import('../../domains/Content/pages/Images/PageImages'));
const PageImagesReserveStatistics = lazy(() => import('../../domains/Content/pages/Images/PageImagesReserveStatistics'));
const PageImagesReserveStatisticsReference = lazy(
  () => import('../../domains/Content/pages/Images/PageImagesReserveStatisticsReference'),
);
const PageImagesSearchSimilar = lazy(() => import('../../domains/Content/pages/Images/PageImagesSearchSimilar'));
const PageDeeplinks = lazy(() => import('../../domains/Other/pages/Deeplinks/PageDeeplinks'));
const PageNews = lazy(() => import('../../domains/Content/pages/Old news/PageNews'));
const PageNewsPriority = lazy(() => import('../../domains/Content/pages/Old news/PageNewsPriority'));
const PageImagesCompressed = lazy(() => import('../../domains/Content/pages/Images/PageImagesCompressed'));
const PageTemplates = lazy(() => import('../../domains/TemplateBuilder/pages/Templates/PageTemplates'));
const PageTemplatesVisualizations = lazy(
  () => import('../../domains/TemplateBuilder/pages/Templates/PageTemplatesVisualizations'),
);
const PageCreateEditTemplatesVisualization = lazy(
  () => import('../../domains/TemplateBuilder/pages/Templates/PageCreateEditTemplatesVisualization'),
);
const PageTemplatesVisualizationDetails = lazy(
  () => import('../../domains/TemplateBuilder/pages/Templates/PageTemplatesVisualizationDetails'),
);
const PageTemplateDetails = lazy(() => import('../../domains/TemplateBuilder/pages/Templates/PageTemplateDetails'));
const PageCreateEditTemplate = lazy(() => import('../../domains/TemplateBuilder/pages/Templates/PageCreateEditTemplate'));
const PageDynamicNews = lazy(() => import('../../domains/Content/pages/News/PageDynamicNews'));
const PageDynamicNewsDetails = lazy(() => import('../../domains/Content/pages/News/PageDynamicNewsDetails'));
const PageDynamicNewsCreate = lazy(() => import('../../domains/Content/pages/News/PageDynamicNewsCreate'));
const PageDynamicNewsEdit = lazy(() => import('../../domains/Content/pages/News/PageDynamicNewsEdit'));
const PageDynamicNewsPriority = lazy(() => import('../../domains/Content/pages/News/PageDynamicNewsPriority'));
const PageAchievements = lazy(() => import('../../domains/Content/pages/Achievements/PageAchievements'));
const PageCategories = lazy(() => import('../../domains/Content/pages/Categories/PageCategories'));
const PageCreateCategory = lazy(() => import('../../domains/Content/pages/Categories/PageCreateCategory'));
const PageEditCategory = lazy(() => import('../../domains/Content/pages/Categories/PageEditCategory'));
const PageCategoryDetails = lazy(() => import('../../domains/Content/pages/Categories/PageCategoryDetails'));
const PageCategoriesPriority = lazy(() => import('../../domains/Content/pages/Categories/PageCategoriesPriority'));
const PageAchievementsEdit = lazy(() => import('../../domains/Content/pages/Achievements/PageAchievementsEdit'));
const PageAchievementDetails = lazy(() => import('../../domains/Content/pages/Achievements/PageAchievementDetails'));
const PageFeatures = lazy(() => import('../../domains/Featured/pages/PageFeatures'));
const PageFeatureCreate = lazy(() => import('../../domains/Featured/pages/PageFeatureCreate'));
const PageFeatureDetails = lazy(() => import('../../domains/Featured/pages/PageFeatureDetails'));
const PageFeaturesEdit = lazy(() => import('../../domains/Featured/pages/PageFeaturesEdit'));
const PageFeaturesPriority = lazy(() => import('../../domains/Featured/pages/PageFeaturesPriority'));
const PagePresets = lazy(() => import('../../domains/TemplateBuilder/pages/Presets/PagePresets'));
const PagePresetsCreate = lazy(() => import('../../domains/TemplateBuilder/pages/Presets/PagePresetsCreate'));
const PagePresetsEdit = lazy(() => import('../../domains/TemplateBuilder/pages/Presets/PagePresetsEdit'));
const PagePresetDetails = lazy(() => import('../../domains/TemplateBuilder/pages/Presets/PagePresetDetails'));
const PagePopups = lazy(() => import('../../domains/Other/pages/Popups/PagePopups'));
const PagePopupDetails = lazy(() => import('../../domains/Other/pages/Popups/PagePopupDetails'));
const PagePopupCreate = lazy(() => import('../../domains/Other/pages/Popups/PagePopupCreate'));
const PagePopupEdit = lazy(() => import('../../domains/Other/pages/Popups/PagePopupEdit'));
const PagePopupsPriority = lazy(() => import('../../domains/Other/pages/Popups/PagePopupsPriority'));
const PageBonuses = lazy(() => import('../../domains/Content/pages/Bonuses/PageBonuses'));
const PageBonusDetails = lazy(() => import('../../domains/Content/pages/Bonuses/PageBonusDetails'));
const PageBonusEdit = lazy(() => import('../../domains/Content/pages/Bonuses/PageBonusEdit'));
const PageLogs = lazy(() => import('../../domains/RBAC/pages/Logs/PageLogs'));
const PageLogDetails = lazy(() => import('../../domains/RBAC/pages/Logs/PageLogDetails'));
const PageCollections = lazy(() => import('../../domains/Content/pages/Collections/PageCollections'));
const PageCreateCollection = lazy(() => import('../../domains/Content/pages/Collections/PageCreateCollection'));
const PageEditCollection = lazy(() => import('../../domains/Content/pages/Collections/PageEditCollection'));
const PageCollectionDetails = lazy(() => import('../../domains/Content/pages/Collections/PageCollectionDetails'));
const PageCollectionPriority = lazy(() => import('../../domains/Content/pages/Collections/PageCollectionPriority'));
const PageFeedsList = lazy(() => import('../../domains/Content/pages/Feeds/PageFeedsList'));
const PageFeedsArchiveAndTrash = lazy(() => import('../../domains/Content/pages/Feeds/PageFeedsArchiveAndTrash'));
const PageFeedsReleased = lazy(() => import('../../domains/Content/pages/Feeds/PageFeedsReleased'));
const PageFeedsPlanning = lazy(() => import('../../domains/Content/pages/Feeds/PageFeedsPlanning'));
const PageFeedsPlanningDetails = lazy(() => import('../../domains/Content/pages/Feeds/PageFeedsPlanningDetails'));
const PageStaticContentResetLogs = lazy(
  () => import('../../domains/Other/pages/StaticContentResetLogs/PageStaticContentResetLogs'),
);
const PageLocalNotifications = lazy(() => import('../../domains/Other/pages/Notitications/Local/PageLocalNotifications'));
const PageCreateLocalNotification = lazy(
  () => import('../../domains/Other/pages/Notitications/Local/PageCreateLocalNotification'),
);
const PageEditLocalNotification = lazy(() => import('../../domains/Other/pages/Notitications/Local/PageEditLocalNotification'));
const PageLocalNotificationDetails = lazy(
  () => import('../../domains/Other/pages/Notitications/Local/PageLocalNotificationDetails'),
);
const PagePushNotifications = lazy(() => import('../../domains/Other/pages/Notitications/Push/PagePushNotifications'));
const PageCreatePushNotification = lazy(() => import('../../domains/Other/pages/Notitications/Push/PageCreatePushNotification'));
const PageEditPushNotification = lazy(() => import('../../domains/Other/pages/Notitications/Push/PageEditPushNotification'));
const PagePushNotificationDetails = lazy(
  () => import('../../domains/Other/pages/Notitications/Push/PagePushNotificationDetails'),
);
const PagePushNotificationsSchedule = lazy(
  () => import('../../domains/Other/pages/Notitications/Push/PagePushNotificationsSchedule'),
);
const PagePushNotificationScheduleDetails = lazy(
  () => import('../../domains/Other/pages/Notitications/Push/PagePushNotificationScheduleDetails'),
);
const PageCreatePushNotificationSchedule = lazy(
  () => import('../../domains/Other/pages/Notitications/Push/PageCreatePushNotificationSchedule'),
);
const PageEditPushNotificationSchedule = lazy(
  () => import('../../domains/Other/pages/Notitications/Push/PageEditPushNotificationSchedule'),
);
const PageFilters = lazy(() => import('../../domains/FilterBuilder/pages/Filter/PageFilters'));
const PageFilterDetails = lazy(() => import('../../domains/FilterBuilder/pages/Filter/PageFilterDetails'));
const PageFilterCreate = lazy(() => import('../../domains/FilterBuilder/pages/Filter/PageFilterCreate'));
const PageFilterEdit = lazy(() => import('../../domains/FilterBuilder/pages/Filter/PageFilterEdit'));
const PageFilterGroups = lazy(() => import('../../domains/FilterBuilder/pages/Groups/PageFilterGroups'));
const PageFilterGroupCreate = lazy(() => import('../../domains/FilterBuilder/pages/Groups/PageFilterGroupCreate'));
const PageFilterGroupDetails = lazy(() => import('../../domains/FilterBuilder/pages/Groups/PageFilterGroupDetails'));
const PageFilterGroupEdit = lazy(() => import('../../domains/FilterBuilder/pages/Groups/PageFilterGroupEdit'));
const PageFilterCollections = lazy(() => import('../../domains/FilterBuilder/pages/Collections/PageFilterCollections'));
const PageFilterCollectionCreate = lazy(() => import('../../domains/FilterBuilder/pages/Collections/PageFilterCollectionCreate'));
const PageFilterCollectionDetails = lazy(
  () => import('../../domains/FilterBuilder/pages/Collections/PageFilterCollectionDetails'),
);
const PageFilterCollectionEdit = lazy(() => import('../../domains/FilterBuilder/pages/Collections/PageFilterCollectionEdit'));
const PageEvents = lazy(() => import('../../domains/Other/pages/Events/PageEvents'));
const PageEventCreate = lazy(() => import('../../domains/Other/pages/Events/PageEventCreate'));
const PageEventDetails = lazy(() => import('../../domains/Other/pages/Events/PageEventDetails'));
const PageEventEdit = lazy(() => import('../../domains/Other/pages/Events/PageEventEdit'));

const routes = [
  {
    path: appRoutes.urlPageLogin,
    element: <PageLogin />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AntdLayout />
      </RequireAuth>
    ),
    children: [
      {
        path: appRoutes.urlPageImages,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageImages,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.LIST]}>
                <PageImages />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageImagesReserveStatistics,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.RESERVE_STATISTICS.LIST]}>
                <PageImagesReserveStatistics />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageImagesReserveStatisticsEdit,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.RESERVE_STATISTICS.EDIT_TABLE_STRUCTURE]}>
                <PageImagesReserveStatistics />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageImagesReserveStatisticsReference,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.RESERVE_STATISTICS.LIST]}>
                <PageImagesReserveStatisticsReference />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageImagesReserveStatisticsReferenceEdit,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.RESERVE_STATISTICS.EDIT_REFERENCES]}>
                <PageImagesReserveStatisticsReference />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageImagesSearchSimilar,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.SEARCH_SIMILAR]}>
                <PageImagesSearchSimilar />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageCategories(),
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageCategories(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.CATEGORIES.LIST]}>
                <PageCategories />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCategoryCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.CATEGORIES.CREATE]}>
                <PageCreateCategory />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCategoryDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.CATEGORIES.VIEW]}>
                <PageCategoryDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCategoryEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.CATEGORIES.EDIT]}>
                <PageEditCategory />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.CATEGORIES }),
        ],
      },
      {
        path: appRoutes.urlPageCategoriesPriority,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.CONTENT.CATEGORIES.PRIORITIES.LIST]}>
            <PageCategoriesPriority />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageBonuses,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageBonuses,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.BONUSES.LIST]}>
                <PageBonuses />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageBonusEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.BONUSES.EDIT]}>
                <PageBonusEdit />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageBonusDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.BONUSES.VIEW]}>
                <PageBonusDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageAchievements,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageAchievements,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.ACHIEVEMENTS.LIST]}>
                <PageAchievements />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageAchievementsDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.ACHIEVEMENTS.VIEW]}>
                <PageAchievementDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageAchievementsEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.ACHIEVEMENTS.EDIT]}>
                <PageAchievementsEdit />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageCollections(),
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageCollections(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.COLLECTIONS.LIST]}>
                <PageCollections />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCollectionDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.COLLECTIONS.VIEW]}>
                <PageCollectionDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCollectionCreate(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.COLLECTIONS.CREATE]}>
                <PageCreateCollection />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCollectionEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.COLLECTIONS.EDIT]}>
                <PageEditCollection />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCollectionPriority,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.COLLECTIONS.PRIORITIES.LIST]}>
                <PageCollectionPriority />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.COLLECTIONS }),
        ],
      },
      {
        path: '/compressed/cargo',
        element: (
          <PrivateRoute allowedPermissions={[Permissions.CONTENT.IMAGES.LIST]}>
            <PageImagesCompressed />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageProfile,
        element: <PageProfile />,
        children: [
          {
            path: appRoutes.urlPageProfile,
            element: <PagePersonalInformation />,
          },
        ],
      },
      {
        path: appRoutes.urlPageRBACUsers,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageRBACUsers,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.USERS.USERS_LIST.VIEW_PAGE]}>
                <PageUsers />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageRBACUserDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.USERS.USER_DETAILS.VIEW]}>
                <PageUserDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageRBACRoles,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageRBACRoles,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.ROLES.ROLES_LIST.VIEW_PAGE]}>
                <PageRoles />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageRBACRoleDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.ROLES.ROLE_DETAILS.VIEW]}>
                <PageRoleDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageRBACPermissions,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.PERMISSIONS.VIEW_LIST]}>
            <PagePermissions />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageRBACLogs,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageRBACLogs,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.LOGS.LIST]}>
                <PageLogs />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageRBACLogDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.ADMINISTRATION.LOGS.DETAILS]}>
                <PageLogDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageLanguages,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.TRANSLATIONS.LANGUAGES.LANGUAGES_LIST.VIEW_PAGE]}>
            <PageLanguages />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageStructures,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageStructures,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TRANSLATIONS.STRUCTURES.LIST.VIEW_PAGE]}>
                <PageStructures />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageStructuresEdit(),
            element: (
              <PrivateRoute
                allowedPermissions={[
                  Permissions.TRANSLATIONS.STRUCTURES.DETAILS.EDIT,
                  Permissions.TRANSLATIONS.STRUCTURES.DETAILS.GET,
                ]}
              >
                <PageStructuresDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageDictionaries(),
        element: <Outlet />,
        children: [...dictionariesRoutes({})],
      },
      {
        path: appRoutes.urlPagePushIcons,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.OTHER.PUSH_ICONS.LIST.VIEW]}>
            <PagePushIcons />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageDeeplinks,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.OTHER.DEEPLINKS.LIST]}>
            <PageDeeplinks />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageLocalNotifications,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageLocalNotifications,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.LOCAL.LIST]}>
                <PageLocalNotifications />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageLocalNotificationCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.LOCAL.CREATE]}>
                <PageCreateLocalNotification />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageLocalNotificationEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.LOCAL.EDIT]}>
                <PageEditLocalNotification />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageLocalNotificationDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.LOCAL.FIND]}>
                <PageLocalNotificationDetails />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.LOCAL_NOTIFICATIONS }),
        ],
      },
      {
        path: appRoutes.urlPagePushNotifications,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPagePushNotifications,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.LIST]}>
                <PagePushNotifications />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.CREATE]}>
                <PageCreatePushNotification />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.EDIT]}>
                <PageEditPushNotification />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.FIND]}>
                <PagePushNotificationDetails />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.PUSH_NOTIFICATIONS }),
        ],
      },
      {
        path: appRoutes.urlPagePushNotificationsSchedule,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPagePushNotificationsSchedule,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.LIST]}>
                <PagePushNotificationsSchedule />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationScheduleCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.CREATE]}>
                <PageCreatePushNotificationSchedule />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationScheduleClone(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.CREATE]}>
                <PageCreatePushNotificationSchedule />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationScheduleEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.EDIT]}>
                <PageEditPushNotificationSchedule />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePushNotificationScheduleDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.FIND]}>
                <PagePushNotificationScheduleDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPagePopups(),
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPagePopups(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.POPUPS.LIST]}>
                <PagePopups />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePopupCreate(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.POPUPS.CREATE]}>
                <PagePopupCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePopupDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.POPUPS.FIND]}>
                <PagePopupDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePopupEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.POPUPS.EDIT]}>
                <PagePopupEdit />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePopupsPriority,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.POPUPS.PRIORITY.LIST]}>
                <PagePopupsPriority />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.POPUPS }),
        ],
      },
      {
        path: appRoutes.urlPageEvents,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageEvents,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.EVENTS.LIST]}>
                <PageEvents />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageEventCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.EVENTS.CREATE]}>
                <PageEventCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageEventDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.EVENTS.FIND]}>
                <PageEventDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageEventEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.OTHER.EVENTS.EDIT]}>
                <PageEventEdit />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.EVENTS }),
          ...collectionsRoutes({ urlPrefix: SOURCES_MAP.EVENTS }),
          ...popupsRoutes({ urlPrefix: SOURCES_MAP.EVENTS }),
          ...featuresRoutes({ urlPrefix: SOURCES_MAP.EVENTS, featureSource: FEATURES_SOURCE_MAP.EXTERNAL_PROMO }),
        ],
      },
      {
        path: appRoutes.urlPageStickers,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageStickers,
            element: (
              <PrivateRoute allowedPermissions={[ Permissions.OTHER.STICKERS.LIST ]}>
                <PageStickers />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageStickerCreate,
            element: (
              <PrivateRoute allowedPermissions={[ Permissions.OTHER.STICKERS.CREATE ]}>
                <PageStickerCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageStickerDetails(),
            element: (
              <PrivateRoute allowedPermissions={[ Permissions.OTHER.STICKERS.FIND ]}>
                <PageStickerDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageStickerEdit(),
            element: (
              <PrivateRoute allowedPermissions={[ Permissions.OTHER.STICKERS.EDIT ]}>
                <PageStickerEdit />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.STICKERS }),
        ],
      },
      {
        path: appRoutes.urlPageFilters,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageFilters,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.FILTERS.LIST]}>
                <PageFilters />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.FILTERS.CREATE]}>
                <PageFilterCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.FILTERS.FIND]}>
                <PageFilterDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.FILTERS.EDIT]}>
                <PageFilterEdit />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.FILTERS }),
        ],
      },
      {
        path: appRoutes.urlPageFilterGroups,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageFilterGroups,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.GROUPS.LIST]}>
                <PageFilterGroups />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterGroupCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.GROUPS.CREATE]}>
                <PageFilterGroupCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterGroupDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.GROUPS.FIND]}>
                <PageFilterGroupDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterGroupEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.GROUPS.EDIT]}>
                <PageFilterGroupEdit />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.FILTER_GROUPS }),
        ],
      },
      {
        path: appRoutes.urlPageFilterCollections,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageFilterCollections,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.COLLECTIONS.LIST]}>
                <PageFilterCollections />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterCollectionCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.COLLECTIONS.CREATE]}>
                <PageFilterCollectionCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterCollectionDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.COLLECTIONS.FIND]}>
                <PageFilterCollectionDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageFilterCollectionEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.FILTER_CONSTRUCTORS.COLLECTIONS.EDIT]}>
                <PageFilterCollectionEdit />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageResetStaticContentLogs,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.OTHER.STATIC_CONTENT_RESET.LIST]}>
            <PageStaticContentResetLogs />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageDynamicNews,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageDynamicNews,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.LIST]}>
                <PageDynamicNews />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageDynamicNewsCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.CREATE]}>
                <PageDynamicNewsCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageDynamicNewsDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.FIND]}>
                <PageDynamicNewsDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageDynamicNewsEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.EDIT]}>
                <PageDynamicNewsEdit />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageDynamicNewsPriority,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.PRIORITY]}>
                <PageDynamicNewsPriority />
              </PrivateRoute>
            ),
          },
          ...dictionariesRoutes({ urlPrefix: SOURCES_MAP.NEWS }),
        ],
      },
      ...(FEATURE_SHOW_OLD_NEWS
        ? [
            {
              path: appRoutes.urlPageNews,
              element: <Outlet />,
              children: [
                {
                  path: appRoutes.urlPageNews,
                  element: (
                    <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.LIST]}>
                      <PageNews />
                    </PrivateRoute>
                  ),
                },
                {
                  path: appRoutes.urlPageNewsPriority,
                  element: (
                    <PrivateRoute allowedPermissions={[Permissions.CONTENT.NEWS.PRIORITY]}>
                      <PageNewsPriority />
                    </PrivateRoute>
                  ),
                },
              ],
            },
          ]
        : []),

      {
        path: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.ARCHIVED }),
        element: (
          <PrivateRoute allowedPermissions={[Permissions.CONTENT.FEEDS.ARCHIVED]}>
            <PageFeedsArchiveAndTrash />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.TRASHED }),
        element: (
          <PrivateRoute allowedPermissions={[Permissions.CONTENT.FEEDS.TRASHED]}>
            <PageFeedsArchiveAndTrash />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.LIST }),
        element: (
          <PrivateRoute allowedPermissions={[Permissions.CONTENT.FEEDS.LIST]}>
            <PageFeedsList />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.RELEASED }),
        element: (
          <PrivateRoute allowedPermissions={[Permissions.CONTENT.FEEDS.RELEASED]}>
            <PageFeedsReleased />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.PLANNING }),
        element: (
          <PrivateRoute
            allowedPermissions={[Permissions.CONTENT.FEEDS.PLANNING.STORAGE, Permissions.CONTENT.FEEDS.PLANNING.BOARD.LIST]}
          >
            <PageFeedsPlanning />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageFeedsPlanningDetails({ feedSource: FEEDS_SOURCE_MAP.PLANNING }),
        element: (
          <PrivateRoute
            allowedPermissions={[Permissions.CONTENT.FEEDS.PLANNING.STORAGE, Permissions.CONTENT.FEEDS.PLANNING.BOARD.DETAILS]}
          >
            <PageFeedsPlanningDetails />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPageFeedsPlanningDetailsEdit({ feedSource: FEEDS_SOURCE_MAP.PLANNING }),
        element: (
          <PrivateRoute
            isMultipleAllowance
            allowedPermissions={[
              Permissions.CONTENT.FEEDS.PLANNING.BOARD.DETAILS,
              Permissions.CONTENT.FEEDS.PLANNING.BOARD.PUBLISH,
            ]}
          >
            <PageFeedsPlanningDetails />
          </PrivateRoute>
        ),
      },

      ...Object.values(FEATURES_SOURCE_MAP).map((featureSource, index) => {
        const sourceKey = Object.keys(FEATURES_SOURCE_MAP)[index];

        return {
          path: appRoutes.urlPageFeatures({ featureSource }),
          element: <Outlet />,
          children: [
            {
              path: appRoutes.urlPageFeatures({ featureSource }),
              element: (
                <PrivateRoute allowedPermissions={[Permissions.FEATURED.FEATURES[sourceKey].LIST]}>
                  <PageFeatures />
                </PrivateRoute>
              ),
            },
            {
              path: appRoutes.urlPageFeatureCreate({ featureSource }),
              element: (
                <PrivateRoute allowedPermissions={[Permissions.FEATURED.FEATURES[sourceKey].CREATE]}>
                  <PageFeatureCreate />
                </PrivateRoute>
              ),
            },
            {
              path: appRoutes.urlPageFeatureDetails({ featureSource }),
              element: (
                <PrivateRoute allowedPermissions={[Permissions.FEATURED.FEATURES[sourceKey].FIND]}>
                  <PageFeatureDetails />
                </PrivateRoute>
              ),
            },
            {
              path: appRoutes.urlPageFeaturesEdit({ featureSource }),
              element: (
                <PrivateRoute allowedPermissions={[Permissions.FEATURED.FEATURES[sourceKey].EDIT]}>
                  <PageFeaturesEdit />
                </PrivateRoute>
              ),
            },
            ...dictionariesRoutes({ urlPrefix: FEATURES_SOURCE_MAP[sourceKey] }),
          ],
        };
      }),
      {
        path: appRoutes.urlPageFeaturePriorities,
        element: (
          <PrivateRoute allowedPermissions={[Permissions.FEATURED.FEATURES.PRIORITY.LIST]}>
            <PageFeaturesPriority />
          </PrivateRoute>
        ),
      },
      {
        path: appRoutes.urlPagePresets(),
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPagePresets(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.PRESETS.LIST]}>
                <PagePresets />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCreatePreset,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.PRESETS.CREATE]}>
                <PagePresetsCreate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePresetEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.PRESETS.EDIT]}>
                <PagePresetsEdit />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPagePresetDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.PRESETS.FIND]}>
                <PagePresetDetails />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageTemplates(),
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageTemplates(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.LIST]}>
                <PageTemplates />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageCreateTemplate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.CREATE]}>
                <PageCreateEditTemplate />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageTemplateDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.FIND]}>
                <PageTemplateDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageEditTemplate(),
            element: (
              <PrivateRoute
                allowedPermissions={[
                  Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.FIND,
                  Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.EDIT,
                ]}
              >
                <PageCreateEditTemplate />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: appRoutes.urlPageTemplatesVisualizations,
        element: <Outlet />,
        children: [
          {
            path: appRoutes.urlPageTemplatesVisualizations,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.LIST]}>
                <PageTemplatesVisualizations />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageTemplatesVisualizationsCreate,
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.CREATE]}>
                <PageCreateEditTemplatesVisualization />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageTemplatesVisualizationsDetails(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.VIEW]}>
                <PageTemplatesVisualizationDetails />
              </PrivateRoute>
            ),
          },
          {
            path: appRoutes.urlPageTemplatesVisualizationsEdit(),
            element: (
              <PrivateRoute allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.EDIT]}>
                <PageCreateEditTemplatesVisualization />
              </PrivateRoute>
            ),
          },
        ],
      },
    ],
  },
  { path: '*', element: <Error404 /> },
];

export default routes;
